/**
 * Treasure chest (`loot.treasure_chest.v1`) — a ground piece: a plank chest with a barrel lid, iron
 * bands, brass corners and a brass lock plate.
 *
 * Frame 96×76, support at the bottom-center (48, 76). `lid` 'closed' | 'open' draws the same chest
 * shut or with the lid thrown back and a warm glint inside, so one recipe serves both states of an
 * interactable chest. `bands` (integer 2..3) iron bands, `wear` (0..1) scuffs and darkened edges,
 * `moss` (0..1) growth at the feet. The only dark marks are the keyhole and, when open, the wide
 * interior — never a two-over-one pattern. Deterministic for the recipe; the seed varies grain,
 * scuffs and moss. No definitions, collision or gameplay geometry.
 */
import { checkEnvelope, checkScope, el, mix, n, partGroup, poly } from '../../svg';
import { rngFor } from '../../rng';
import type { PieceArt, PieceOpening, PieceRecipe, ResolvedPalette } from '../../contract';

export type ChestLid = 'closed' | 'open';
export interface TreasureChestParams {
  readonly lid: ChestLid;
  readonly bands: number; // integer 2..3
  readonly wear: number;  // 0..1
  readonly moss: number;  // 0..1
}
export type TreasureChestRecipe = PieceRecipe<'loot.treasure_chest.v1', TreasureChestParams>;

export const TREASURE_CHEST_FRAME = { w: 96, h: 76 } as const;

export const LOOT_TREASURE_CHEST: TreasureChestRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'loot.treasure_chest.default.v1', generatorId: 'loot.treasure_chest.v1',
  seed: 20071101, paletteId: 'nook_warm', params: { lid: 'closed', bands: 3, wear: 0.35, moss: 0.3 },
};

const W = TREASURE_CHEST_FRAME.w, H = TREASURE_CHEST_FRAME.h;
const LEFT = 12, RIGHT = 84, RIM = 44, BASE = 71; // body box; RIM is the lid seam
const LID_TOP = 22;                              // crown of the closed barrel lid

function amount(p: unknown, key: string, lo: number, hi: number, integer = false): number {
  const v = (p as Record<string, unknown>)[key];
  if (typeof v !== 'number' || !(v >= lo && v <= hi) || (integer && !Number.isInteger(v))) {
    throw new Error(`loot.treasure_chest.v1: ${key} must be ${integer ? 'an integer ' : ''}${lo}..${hi}`);
  }
  return v;
}

/** Decorrelate short channel names in the shared seeded RNG. */
function grain(recipe: { seed: number; id: string }): (channel: string) => number {
  const random = rngFor(recipe);
  return channel => {
    let h = Math.floor(random(channel) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

const stroke = (d: string, color: string, width: number) =>
  el('path', { d, fill: 'none', stroke: color, 'stroke-width': width, 'stroke-linecap': 'round' });
const rivet = (x: number, y: number, p: ResolvedPalette, r = 1.5) =>
  el('circle', { cx: x, cy: y, r, fill: p.warmCore, stroke: p.outline, 'stroke-width': 0.6 });

export function buildTreasureChest(recipe: TreasureChestRecipe, p: ResolvedPalette, scope: string): PieceArt {
  checkEnvelope(recipe, LOOT_TREASURE_CHEST.generatorId); checkScope(scope);
  const { lid } = recipe.params;
  if (lid !== 'closed' && lid !== 'open') throw new Error('loot.treasure_chest.v1: lid must be closed or open');
  const bands = amount(recipe.params, 'bands', 2, 3, true);
  const wear = amount(recipe.params, 'wear', 0, 1), moss = amount(recipe.params, 'moss', 0, 1);
  const random = grain(recipe), parts: string[] = [], openings: PieceOpening[] = [];
  const iron = mix(p.outline, p.stoneShadow, 0.35), brass = mix(p.warmCore, p.paperShadow, 0.45);
  const plank = mix(p.woodBase, p.woodShadow, wear * 0.35);
  const bandXs = bands === 3 ? [LEFT + 14, 48, RIGHT - 14] : [LEFT + 18, RIGHT - 18];

  // Ground shadow and feet.
  const ground = el('ellipse', { cx: W / 2, cy: H - 3, rx: 44, ry: 3.5, fill: mix(p.outline, p.stoneShadow, 0.55) })
    + el('path', { d: poly([{ x: LEFT, y: BASE }, { x: LEFT + 9, y: BASE }, { x: LEFT + 8, y: H - 2 }, { x: LEFT + 1, y: H - 2 }]), fill: p.woodShadow, stroke: p.outline, 'stroke-width': 1 })
    + el('path', { d: poly([{ x: RIGHT - 9, y: BASE }, { x: RIGHT, y: BASE }, { x: RIGHT - 1, y: H - 2 }, { x: RIGHT - 8, y: H - 2 }]), fill: p.woodShadow, stroke: p.outline, 'stroke-width': 1 });

  // Body: three planks, scuffs and darkened edges by wear.
  let body = el('rect', { x: LEFT, y: RIM, width: RIGHT - LEFT, height: BASE - RIM, fill: plank, stroke: p.outline, 'stroke-width': 1.6 });
  for (const y of [RIM + 9, RIM + 18]) body += stroke(`M${LEFT + 1} ${y}H${RIGHT - 1}`, p.woodShadow, 1);
  body += stroke(`M${LEFT + 2} ${RIM + 2}H${RIGHT - 2}`, p.woodLight, 1.4);
  for (let i = 0; i < Math.round(2 + wear * 5); i++) {
    const x = LEFT + 6 + random(`scuff.x.${i}`) * (RIGHT - LEFT - 16), y = RIM + 4 + random(`scuff.y.${i}`) * (BASE - RIM - 8);
    body += stroke(`M${n(x)} ${n(y)}l${n(4 + random(`scuff.l.${i}`) * 6)} ${n(random(`scuff.d.${i}`) - 0.5)}`, mix(p.woodShadow, p.outline, 0.3), 0.8);
  }
  if (wear > 0) body += el('rect', { x: LEFT + 1, y: BASE - 5, width: RIGHT - LEFT - 2, height: 4, fill: p.woodShadow, opacity: n(0.25 + wear * 0.35) });

  // Lid: a barrel arc when closed; thrown back past the hinge when open, showing the warm inside.
  let lidArt = '';
  const RX = (RIGHT - LEFT + 2) / 2, RY = RIM - LID_TOP, CX = W / 2;
  const arcY = (x: number, ry: number) => RIM - ry * Math.sqrt(Math.max(0, 1 - ((x - CX) / RX) ** 2));
  if (lid === 'closed') {
    // A barrel lid: half an ellipse, two curved planks, bands that end at its edge.
    const arc = (ry: number) => `M${LEFT - 1} ${RIM}A${n(RX)} ${n(ry)} 0 0 1 ${RIGHT + 1} ${RIM}`;
    lidArt = el('path', { d: arc(RY) + 'Z', fill: mix(plank, p.woodLight, 0.18), stroke: p.outline, 'stroke-width': 1.6 })
      + stroke(`M${LEFT + 3} ${RIM - 1}A${n(RX - 4)} ${n(RY * 0.62)} 0 0 1 ${RIGHT - 3} ${RIM - 1}`, p.woodShadow, 1)
      + stroke(`M${CX - 22} ${n(arcY(CX - 22, RY) + 3)}A${n(RX - 8)} ${n(RY - 5)} 0 0 1 ${CX + 14} ${n(arcY(CX + 14, RY) + 2.4)}`, p.woodLight, 1.3);
    for (const x of bandXs) lidArt += el('path', { d: `M${x - 2} ${RIM}V${n(arcY(x - 2, RY) + 0.6)}L${x + 2} ${n(arcY(x + 2, RY) + 0.6)}V${RIM}Z`, fill: iron });
    lidArt += stroke(`M${LEFT - 1} ${RIM}H${RIGHT + 1}`, p.outline, 2);
  } else {
    // The lid thrown back past the hinge: its darker underside, bands and rim, with a warm glint inside.
    const mouth = { x: LEFT + 3, y: RIM - 6, width: RIGHT - LEFT - 6, height: 9, fill: mix(p.woodShadow, p.outline, 0.55) };
    openings.push(mouth);
    const lidTop = 15, underside = mix(p.woodShadow, plank, 0.35);
    lidArt = el('path', { d: `M${LEFT} ${RIM - 6}L${LEFT + 3} ${lidTop + 3}Q${CX} ${lidTop - 5} ${RIGHT - 3} ${lidTop + 3}L${RIGHT} ${RIM - 6}Z`, fill: underside, stroke: p.outline, 'stroke-width': 1.6 })
      + stroke(`M${LEFT + 5} ${lidTop + 6}Q${CX} ${lidTop - 1} ${RIGHT - 5} ${lidTop + 6}`, mix(underside, p.woodLight, 0.5), 1.4)
      + stroke(`M${LEFT + 4} ${RIM - 14}H${RIGHT - 4}`, p.woodShadow, 1);
    for (const x of bandXs) lidArt += stroke(`M${x} ${RIM - 7}L${x + (x - CX) * 0.04} ${lidTop + 2 + Math.abs(x - CX) * 0.08}`, iron, 3.6);
    lidArt += el('rect', mouth)
      + el('ellipse', { cx: CX, cy: RIM - 3, rx: 31, ry: 6, fill: mix(p.warmCore, p.paperLight, 0.3), opacity: 0.55 })
      + el('ellipse', { cx: CX, cy: RIM - 3, rx: 25, ry: 3.6, fill: p.warmCore });
    for (let i = 0; i < 6; i++) {
      lidArt += el('ellipse', { cx: 29 + i * 7.6 + random(`coin.${i}`) * 2.5, cy: RIM - 3.5 - random(`coin.h.${i}`) * 2.2, rx: 2.6, ry: 1.7, fill: brass, stroke: p.paperShadow, 'stroke-width': 0.5 });
    }
    lidArt += el('path', { d: `M${CX} ${RIM - 12}l1.4 3.6l3.6 1.4l-3.6 1.4l-1.4 3.6l-1.4 -3.6l-3.6 -1.4l3.6 -1.4Z`, fill: '#FFFFFF', opacity: 0.9 });
  }

  // Iron bands, brass corners and rivets on the body.
  let metal = '';
  for (const x of bandXs) metal += el('rect', { x: x - 2, y: RIM, width: 4, height: BASE - RIM, fill: iron }) + rivet(x, RIM + 5, p, 1.1) + rivet(x, BASE - 5, p, 1.1);
  for (const [x, sx] of [[LEFT, 1], [RIGHT, -1]] as const) {
    metal += el('path', { d: poly([{ x, y: RIM }, { x: x + sx * 8, y: RIM }, { x, y: RIM + 8 }]), fill: brass, stroke: p.outline, 'stroke-width': 0.8 })
      + el('path', { d: poly([{ x, y: BASE }, { x: x + sx * 8, y: BASE }, { x, y: BASE - 8 }]), fill: brass, stroke: p.outline, 'stroke-width': 0.8 });
  }

  // Lock plate with a single keyhole; the hasp hangs loose when the chest is open.
  const keyhole = { x: 46.6, y: RIM + 5, width: 2.8, height: 5, fill: mix(p.outline, '#000000', 0.25) };
  openings.push(keyhole);
  let lock = el('path', { d: `M41 ${RIM - 1}H55V${RIM + 11}Q48 ${RIM + 15} 41 ${RIM + 11}Z`, fill: brass, stroke: p.outline, 'stroke-width': 1 })
    + el('rect', keyhole) + el('circle', { cx: 48, cy: RIM + 5, r: 1.9, fill: keyhole.fill });
  lock += lid === 'closed'
    ? el('path', { d: `M44 ${RIM - 7}H52V${RIM}H44Z`, fill: brass, stroke: p.outline, 'stroke-width': 0.9 })
    : el('path', { d: `M44 ${RIM + 11}H52V${RIM + 18}H44Z`, fill: brass, stroke: p.outline, 'stroke-width': 0.9, opacity: 0.95 });

  // Moss tufts at the feet.
  let growth = '';
  for (let i = 0; i < Math.round(moss * 6); i++) {
    const side = i % 2 ? RIGHT - 6 - random(`moss.x.${i}`) * 10 : LEFT + 2 + random(`moss.x.${i}`) * 10;
    growth += el('ellipse', { cx: side, cy: H - 4 - random(`moss.y.${i}`) * 3, rx: 3 + random(`moss.r.${i}`) * 3, ry: 2, fill: i % 3 ? p.moss : mix(p.moss, p.reedLight, 0.4) });
  }

  const art = partGroup(parts, 'chest-ground', ground) + partGroup(parts, 'chest-body', body)
    + partGroup(parts, 'chest-lid', lidArt) + partGroup(parts, 'chest-metal', metal)
    + partGroup(parts, 'chest-lock', lock) + (growth ? partGroup(parts, 'chest-moss', growth) : '');
  return {
    frame: TREASURE_CHEST_FRAME, body: art, parts, openings,
    anchors: { support: { x: W / 2, y: H }, lid_hinge: { x: W / 2, y: RIM }, loot: { x: W / 2, y: RIM - 4 } },
  };
}
